// The last stage of a sale, for the seller: the sale is complete. It is where the seller's own
// statement that the change of ownership is reported leads — and nowhere else — so a seller who
// has not made that statement, or a buyer, is sent back to the stage that is theirs.
//
// The buyer's way on from a reported transfer is the insurance, which this app does not have yet;
// until it does the buyer stays on the Ägarbyte page, which already says what was stated.
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ownershipApi } from "./ownershipApi";
import { DEAL_HOME_PATH } from "../../../lib/dealSession";
import SellerFlowTrack from "../SellerFlowTrack";
import DealClosing from "./DealClosing";
import { OWNERSHIP_TRACK } from "./DealOwnershipStage";
import { OWNERSHIP_CONTENT_MAX } from "./OwnershipParts";

// As on the stages before it: the page holds the router and passes the move in. The fallback is a
// real navigation, so a page that forgets still works.
const defaultAdvance = (to) => { window.location.assign(to); };

// `onAdvance` REPLACES the current entry (a redirect to the stage that is the user's own);
// `onNavigate` PUSHES one (a door the user chose, so Back returns to the closing page).
export default function DealCompletionStage({ contractId, deal, onAdvance = defaultAdvance, onNavigate = onAdvance }) {
  const { t } = useTranslation();
  const [state, setState] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const next = await ownershipApi.getState(contractId);
        if (!cancelled) setState(next);
      } catch (_) {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [contractId]);

  // Where this user belongs, if not here. A deal that cannot be read, or that this user is no party
  // to, belongs on the overview; a payment not yet confirmed on the payment stage; anything short
  // of the seller's own statement on the Ägarbyte stage.
  const target = failed ? DEAL_HOME_PATH
    : !state ? null
    : state.stage === "notOpen"
      ? (state.notOpenReason === "paymentNotConfirmed" ? `/Payment/${contractId}` : DEAL_HOME_PATH)
      : state.role !== "seller" || state.stage !== "reported" ? `/Ownership/${contractId}`
      : null;

  const sentTo = useRef(null);
  useEffect(() => {
    if (!target || sentTo.current === target) return;
    sentTo.current = target;
    onAdvance(target);
  }, [target, onAdvance]);

  if (!state || target) {
    return (
      <div className="mx-auto w-full" style={{ maxWidth: OWNERSHIP_CONTENT_MAX }} role="status" aria-busy="true"
        data-testid="completion-preparing">
        <span className="sr-only">{t("completion.preparing")}</span>
        <div className="border-t-2 border-ds-ink pt-4" aria-hidden="true">
          <div className="h-6 w-28 animate-pulse rounded bg-[rgba(20,36,46,.08)]" />
        </div>
        <div className="mt-8 h-7 w-2/3 animate-pulse rounded bg-[rgba(20,36,46,.08)]" aria-hidden="true" />
        <div className="mt-4 h-4 w-1/2 animate-pulse rounded bg-[rgba(20,36,46,.08)]" aria-hidden="true" />
      </div>
    );
  }

  return (
    <>
      {/* The rail at its terminal position: every stage is behind the seller. */}
      <div className="flow-shell-track">
        <SellerFlowTrack terminal activeStep={0} t={t} milestones={OWNERSHIP_TRACK} ns="seller" />
      </div>
      <DealClosing state={state} deal={deal} contractId={contractId} onNavigate={onNavigate} />
    </>
  );
}
